import { ActionIcon, Box, Group, Loader, Select, SimpleGrid, Text, UnstyledButton } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { PacIntroCallSlotSelection } from '@bariendo/lib/patient-access-note';
import { PatientTimeZone, describeTimeZoneId, timeZoneOptions } from '@bariendo/lib/patient-time-zone';
import { ConsultSlot, consultSlotKey } from './useNpConsultSlots';

// Extended here as well as in `main.tsx`: these read `.tz()` on the patient's
// zone, and a unit test rendering them does not boot the app. `extend` is
// idempotent.
dayjs.extend(utc);
dayjs.extend(timezone);

interface PacIntroCallSlotGridProps {
  slots: ConsultSlot[];
  loading: boolean;
  /**
   * The zone every time on this grid is rendered in — the patient's, from their
   * ZIP. Optional: without it the grid falls back to the browser's zone and says
   * nothing, which is how it behaved before BAR-2612.
   */
  timeZone?: PatientTimeZone;
  /** What the ZIP derived, before any override — decides what the hint says. */
  derivedTimeZone?: PatientTimeZone;
  /** Lets the rep correct the zone when the ZIP is stale or the caller says otherwise. */
  onTimeZoneChange?: (timeZone: string) => void;
  dayOffset: number;
  maxDayOffset: number;
  onDayChange: (offset: number) => void;
  selected?: PacIntroCallSlotSelection;
  onSelect: (slot: ConsultSlot) => void;
  disabled?: boolean;
}

function dayLabel(day: dayjs.Dayjs, offset: number): string {
  if (offset === 0) return 'Today';
  if (offset === 1) return 'Tomorrow';
  // Two weeks hold two of every weekday, so the full name plus the date below.
  return day.format('dddd');
}

/**
 * Live availability for the free provider consultation, stepped a day at a time.
 *
 * Times are the **patient's**, not the rep's. A rep reading 2:00pm off this grid
 * to a caller two zones away books a consultation for an hour neither of them
 * meant, so the zone is stated on the grid rather than inferred from it — and
 * `Today` means the caller's today, which is why the day comes from the zone
 * too.
 */
export function PacIntroCallSlotGrid(props: PacIntroCallSlotGridProps) {
  const {
    slots,
    loading,
    timeZone,
    derivedTimeZone,
    onTimeZoneChange,
    dayOffset,
    maxDayOffset,
    onDayChange,
    selected,
    onSelect,
    disabled,
  } = props;
  const inZone = (iso?: string) => (timeZone ? dayjs(iso).tz(timeZone.timeZone) : dayjs(iso));
  const day = inZone().add(dayOffset, 'day');

  /*
    The zone is a control, not a caption. The ZIP is a good first guess and a bad
    last word — charts hold stale addresses and callers move — so the rep can
    correct it and the whole grid follows. The hint says where the current value
    came from, because "Eastern" that was assumed and "Eastern" that was derived
    are different facts and only one of them needs checking.
  */
  const zoneHint = !timeZone
    ? undefined
    : derivedTimeZone && timeZone.timeZone !== derivedTimeZone.timeZone
      ? `changed from ${describeTimeZoneId(derivedTimeZone.timeZone, day.toDate())}`
      : timeZone.fromZip
        ? 'from their ZIP'
        : 'no ZIP on file — Eastern assumed';

  return (
    <Box my="xs">
      {timeZone && (
        <Group gap={8} wrap="nowrap" align="center" mb={6}>
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
            Times shown in
          </Text>
          <Select
            size="xs"
            w={150}
            allowDeselect={false}
            disabled={disabled || !onTimeZoneChange}
            aria-label="Time zone these times are shown in"
            // The effective zone as the fallback, not just the derived one: the
            // two props are independently optional, and a value the list does not
            // offer renders the Select empty.
            data={timeZoneOptions(derivedTimeZone?.timeZone ?? timeZone.timeZone).map((option) => ({
              value: option.timeZone,
              label: describeTimeZoneId(option.timeZone, day.toDate()),
            }))}
            value={timeZone.timeZone}
            onChange={(value) => value && onTimeZoneChange?.(value)}
          />
          {zoneHint && (
            <Text size="xs" c="dimmed" truncate>
              {zoneHint}
            </Text>
          )}
        </Group>
      )}
      <Group justify="space-between" mb={6}>
        <ActionIcon
          variant="default"
          size="sm"
          aria-label="Previous day"
          disabled={dayOffset === 0}
          onClick={() => onDayChange(dayOffset - 1)}
        >
          <IconChevronLeft size={14} />
        </ActionIcon>
        <Text size="sm" fw={600}>
          {dayLabel(day, dayOffset)}{' '}
          <Text component="span" size="sm" fw={400} c="dimmed">
            {day.format('MMM D')}
          </Text>
        </Text>
        <ActionIcon
          variant="default"
          size="sm"
          aria-label="Next day"
          disabled={dayOffset >= maxDayOffset}
          onClick={() => onDayChange(dayOffset + 1)}
        >
          <IconChevronRight size={14} />
        </ActionIcon>
      </Group>

      {loading && (
        <Group justify="center" py="sm">
          <Loader size="xs" />
        </Group>
      )}

      {!loading && slots.length === 0 && (
        <Text size="xs" c="dimmed" ta="center" py="sm">
          No consultation times available on this day.
        </Text>
      )}

      {!loading && slots.length > 0 && (
        <SimpleGrid cols={4} spacing={5}>
          {slots.map((slot) => {
            const isSelected = selected ? consultSlotKey(selected) === consultSlotKey(slot) : false;
            return (
              <UnstyledButton
                key={consultSlotKey(slot)}
                disabled={disabled}
                aria-pressed={isSelected}
                onClick={() => onSelect(slot)}
                style={{
                  padding: '5px 4px',
                  borderRadius: 8,
                  border: `1px solid ${isSelected ? 'var(--mantine-color-green-9)' : 'var(--mantine-color-gray-3)'}`,
                  background: isSelected ? 'rgba(36,55,33,0.10)' : 'transparent',
                  textAlign: 'center',
                }}
              >
                <Text size="xs" fw={isSelected ? 600 : 400} c={isSelected ? 'green.9' : undefined}>
                  {inZone(slot.start).format('h:mma')}
                </Text>
                {slot.practitionerName && (
                  <Text size="10px" c={isSelected ? 'green.9' : 'dimmed'} truncate>
                    {slot.practitionerName}
                  </Text>
                )}
              </UnstyledButton>
            );
          })}
        </SimpleGrid>
      )}
    </Box>
  );
}
